<template>
  <el-tooltip :content="content" :placement="placement" :disabled="!isOverflow" effect="dark"
    :popper-class="dynamicTooltipClass">
    <div class="text-tooltip-content" :style="contentStyle" ref="textRef">
      {{ content }}
    </div>
  </el-tooltip>
</template>

<script>
export default {
  name: 'TextTooltip',
  props: {
    // 显示的文本内容
    content: {
      type: String,
      default: ''
    },
    // 最大显示宽度，单位 px
    maxWidth: {
      type: Number,
      default: 200
    },
    // 最大显示字符数（可选，如果设置了则按字符数截断）
    maxLength: {
      type: Number,
      default: 0
    },
    // tooltip 显示位置
    placement: {
      type: String,
      default: 'top'
    },
    // tooltip 弹出层的最大宽度，单位 px
    tooltipMaxWidth: {
      type: Number,
      default: 400
    },
    // 自定义 tooltip class
    tooltipClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isOverflow: false,
      uniqueId: ''
    }
  },
  mounted() {
    // 生成唯一的 class ID
    this.uniqueId = 'text-tooltip-' + Math.random().toString(36).slice(2, 11)
    this.addDynamicStyle()
    // 监听窗口大小变化
    window.addEventListener('resize', this.checkOverflow)
    this.$nextTick(() => {
      this.checkOverflow()
    })
  },
  beforeDestroy() {
    // 清理事件监听器
    window.removeEventListener('resize', this.checkOverflow)
    // 清理动态样式
    this.cleanupDynamicStyle()
  },
  computed: {
    contentStyle() {
      const style = {
        maxWidth: this.maxWidth + 'px'
      }
      // 如果设置了 maxLength，则使用字符串截断
      if (this.maxLength > 0) {
        style.maxWidth = 'none'
      }
      return style
    },
    displayContent() {
      if (this.maxLength > 0 && this.content && this.content.length > this.maxLength) {
        return this.content.slice(0, this.maxLength) + '...'
      }
      return this.content
    },
    // 动态 tooltip class
    dynamicTooltipClass() {
      return this.uniqueId ? `${this.uniqueId} ${this.tooltipClass}` : this.tooltipClass
    }
  },
  watch: {
    content: {
      handler() {
        this.$nextTick(() => {
          this.checkOverflow()
        })
      },
      immediate: true
    },
    tooltipMaxWidth: {
      handler() {
        this.updateDynamicStyle()
      }
    }
  },
  methods: {
    checkOverflow() {
      if (!this.$refs.textRef) return

      const element = this.$refs.textRef
      // 检查内容是否溢出
      this.isOverflow = element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight
    },
    // 添加动态样式
    addDynamicStyle() {
      if (!document.getElementById('text-tooltip-dynamic-style')) {
        const style = document.createElement('style')
        style.id = 'text-tooltip-dynamic-style'
        document.head.appendChild(style)
      }
      this.updateDynamicStyle()
    },
    // 更新动态样式
    updateDynamicStyle() {
      const styleEl = document.getElementById('text-tooltip-dynamic-style')
      if (styleEl && this.uniqueId) {
        const cssRules = styleEl.sheet && styleEl.sheet.cssRules

        // 删除所有相关的旧规则
        if (cssRules) {
          const rulesToDelete = []
          for (let i = 0; i < cssRules.length; i++) {
            if (cssRules[i].cssText && cssRules[i].cssText.includes(`.${this.uniqueId}`)) {
              rulesToDelete.push(i)
            }
          }
          // 从后往前删除，避免索引变化
          for (let i = rulesToDelete.length - 1; i >= 0; i--) {
            if (styleEl.sheet) {
              styleEl.sheet.deleteRule(rulesToDelete[i])
            }
          }
        }

        const maxWidth = this.tooltipMaxWidth || 400

        // 添加多条规则以确保样式生效
        const rules = [
          `div.${this.uniqueId}`,
          `div.el-tooltip__popper.${this.uniqueId}`,
          `.${this.uniqueId}[x-placement*="bottom"]`,
          `.${this.uniqueId}[x-placement*="top"]`,
          `.${this.uniqueId}[x-placement*="left"]`,
          `.${this.uniqueId}[x-placement*="right"]`
        ]

        rules.forEach(selector => {
          const cssRule = `
            ${selector} {
              max-width: ${maxWidth}px !important;
              word-break: break-word !important;
              white-space: normal !important;
            }
          `
          if (styleEl.sheet) {
            const rulesLength = styleEl.sheet.cssRules ? styleEl.sheet.cssRules.length : 0
            styleEl.sheet.insertRule(cssRule, rulesLength)
          }
        })
      }
    },
    // 清理动态样式
    cleanupDynamicStyle() {
      const styleEl = document.getElementById('text-tooltip-dynamic-style')
      if (styleEl && this.uniqueId && styleEl.sheet) {
        const cssRules = styleEl.sheet.cssRules
        if (cssRules) {
          const rulesToDelete = []
          for (let i = 0; i < cssRules.length; i++) {
            if (cssRules[i].cssText && cssRules[i].cssText.includes(`.${this.uniqueId}`)) {
              rulesToDelete.push(i)
            }
          }
          // 从后往前删除，避免索引变化
          for (let i = rulesToDelete.length - 1; i >= 0; i--) {
            styleEl.sheet.deleteRule(rulesToDelete[i])
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.text-tooltip-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
  word-break: break-all;
}
</style>

<style>
/* Tooltip 弹出层基础样式 */
.el-tooltip__popper {
  word-break: break-word !important;
  white-space: normal !important;
}

/* 确保所有 TextTooltip 组件的弹出层都有合理的宽度 */
.el-tooltip__popper[class*="text-tooltip-"] {
  max-width: 400px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/TextTooltip.vue